const HEX_3 = /^#?([0-9a-f]{3})$/i
const HEX_6 = /^#?([0-9a-f]{6})$/i

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)))
}

export function normalizeHexColor(value, fallback) {
  const raw = String(value ?? '').trim()
  const short = raw.match(HEX_3)
  if (short) {
    const [r, g, b] = short[1].split('')
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
  }
  const full = raw.match(HEX_6)
  if (full) return `#${full[1]}`.toUpperCase()
  return fallback
}

function hexToRgb(hex) {
  const normalized = normalizeHexColor(hex, '#000000')
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  }
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((channel) => clampChannel(channel).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase()
}

function mixHex(a, b, amount) {
  const x = hexToRgb(a)
  const y = hexToRgb(b)
  const t = Math.max(0, Math.min(1, amount))
  return rgbToHex({
    r: x.r + (y.r - x.r) * t,
    g: x.g + (y.g - x.g) * t,
    b: x.b + (y.b - x.b) * t,
  })
}

function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex)
  const channels = [r, g, b].map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722
}

function readableTextColor(hex) {
  return relativeLuminance(hex) > 0.18 ? '#111827' : '#FFFFFF'
}

function translateBrandColorForDark(hex) {
  const lum = relativeLuminance(hex)
  if (lum < 0.16) return mixHex(hex, '#FFFFFF', 0.58)
  if (lum < 0.36) return mixHex(hex, '#FFFFFF', 0.42)
  if (lum > 0.74) return mixHex(hex, '#7DD3FC', 0.28)
  return mixHex(hex, '#FFFFFF', 0.22)
}

export function derivePortalThemeColors(primaryColor, secondaryColor, dark = false) {
  const primary = normalizeHexColor(primaryColor, '#00838F')
  const secondary = normalizeHexColor(secondaryColor, '#E0F2F1')

  if (!dark) {
    return {
      primary,
      secondary,
      onPrimary: readableTextColor(primary),
      onSecondary: readableTextColor(secondary),
      surface: '#FFFFFF',
      background: '#F7F9FB',
      onSurface: '#1F2933',
    }
  }

  const darkPrimary = translateBrandColorForDark(primary)
  const darkSecondary = translateBrandColorForDark(secondary)

  return {
    primary: darkPrimary,
    secondary: darkSecondary,
    onPrimary: readableTextColor(darkPrimary),
    onSecondary: readableTextColor(darkSecondary),
    surface: '#151B23',
    background: '#0F141B',
    onSurface: '#E6EDF3',
  }
}
